/**
 * V111: MM 월 선택기 컴포넌트
 * V130.3: 년/월 선택 + 이동 버튼 추가 (빠른 월 점프)
 * V130.3.1: UI 레이아웃 조정
 *   - < > 버튼 좌측, 년/월/이동 우측
 *   - 년/월/이동을 하나의 박스로 그룹화
 *   - 이동 버튼 화살표 제거
 *
 * ◀ ▶ 버튼으로 월 이동
 * Year/Month select + 이동 버튼으로 직접 점프
 */

import React, { useState, useMemo, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { formatMonthKo, addMonths, parseMonth } from '../../../shared/types/mm';

/** V54.10: 공휴일 정보 타입 */
interface HolidayInfo {
  date: string;
  name: string;
}

interface MmMonthSelectorProps {
  /** 현재 선택된 월 (YYYY-MM) */
  month: string;
  /** 월 변경 핸들러 */
  onMonthChange: (month: string) => void;
  /** 해당월 근무일수 */
  workingDays?: number;
  /** V54.10: 해당월 공휴일 목록 */
  holidays?: HolidayInfo[];
  /** 비활성화 여부 */
  disabled?: boolean;
}

/**
 * V130.3: 년도 범위 생성 (현재년도 기준 ±2년)
 * - 업무 특성상 과거/미래 2년 범위면 충분
 * - 너무 넓은 범위는 UX 저하
 */
function getYearRange(): number[] {
  const currentYear = new Date().getFullYear();
  const years: number[] = [];
  for (let y = currentYear - 2; y <= currentYear + 2; y++) {
    years.push(y);
  }
  return years;
}

/**
 * MM 월 선택기
 */
export function MmMonthSelector({
  month,
  onMonthChange,
  workingDays,
  holidays = [],
  disabled = false,
}: MmMonthSelectorProps) {
  // V130.3: 현재 표시 중인 월 파싱
  const currentParsed = useMemo(() => parseMonth(month), [month]);

  // V130.3: 선택 필터 상태 (select만 변경해도 이동하지 않음)
  const [filterYear, setFilterYear] = useState(currentParsed.year);
  const [filterMonth, setFilterMonth] = useState(currentParsed.month);

  // V130.3: 월이 외부에서 변경되면 필터 상태도 동기화
  useEffect(() => {
    setFilterYear(currentParsed.year);
    setFilterMonth(currentParsed.month);
  }, [currentParsed.year, currentParsed.month]);

  // V130.3: 년도 범위 (±2년)
  const yearOptions = useMemo(() => getYearRange(), []);

  const handlePrev = () => {
    if (!disabled) {
      onMonthChange(addMonths(month, -1));
    }
  };

  const handleNext = () => {
    if (!disabled) {
      onMonthChange(addMonths(month, 1));
    }
  };

  // V130.3: 이동 버튼 클릭 시 선택된 년/월로 점프
  const handleJump = () => {
    if (disabled) return;
    const targetMonth = `${filterYear}-${String(filterMonth).padStart(2, '0')}`;
    if (targetMonth !== month) {
      onMonthChange(targetMonth);
    }
  };

  // V130.3: 선택한 년/월이 현재 표시 중인 월과 다른지 확인
  const isFilterDifferent = useMemo(() => {
    const targetMonth = `${filterYear}-${String(filterMonth).padStart(2, '0')}`;
    return targetMonth !== month;
  }, [filterYear, filterMonth, month]);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        marginBottom: '24px',
        padding: '16px',
        backgroundColor: 'var(--color-gray-50)',
        borderRadius: '8px',
        flexWrap: 'wrap',
      }}
    >
      {/* V130.3.1: < > 버튼 + 현재 월 표시 (좌측) */}
      <button
        onClick={handlePrev}
        disabled={disabled}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '36px',
          height: '36px',
          border: '1px solid var(--color-gray-300)',
          borderRadius: '6px',
          backgroundColor: 'white',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
        }}
        title="이전 월"
      >
        <ChevronLeft size={20} />
      </button>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '18px',
          fontWeight: 600,
          color: 'var(--color-gray-900)',
        }}
      >
        <Calendar size={20} style={{ color: 'var(--color-primary-600)' }} />
        <span>{formatMonthKo(month)}</span>
      </div>

      <button
        onClick={handleNext}
        disabled={disabled}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '36px',
          height: '36px',
          border: '1px solid var(--color-gray-300)',
          borderRadius: '6px',
          backgroundColor: 'white',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
        }}
        title="다음 월"
      >
        <ChevronRight size={20} />
      </button>

      {/* V130.3.1: 년/월 선택 + 이동 버튼 (박스로 그룹화) */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          backgroundColor: 'white',
          border: '1px solid var(--color-gray-300)',
          borderRadius: '8px',
        }}
      >
        {/* 년도 선택 */}
        <select
          value={filterYear}
          onChange={(e) => setFilterYear(Number(e.target.value))}
          disabled={disabled}
          style={{
            padding: '6px 10px',
            fontSize: '14px',
            fontWeight: 500,
            border: '1px solid var(--color-gray-200)',
            borderRadius: '4px',
            backgroundColor: 'var(--color-gray-50)',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1,
          }}
        >
          {yearOptions.map((y) => (
            <option key={y} value={y}>
              {y}년
            </option>
          ))}
        </select>

        {/* 월 선택 */}
        <select
          value={filterMonth}
          onChange={(e) => setFilterMonth(Number(e.target.value))}
          disabled={disabled}
          style={{
            padding: '6px 10px',
            fontSize: '14px',
            fontWeight: 500,
            border: '1px solid var(--color-gray-200)',
            borderRadius: '4px',
            backgroundColor: 'var(--color-gray-50)',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1,
          }}
        >
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
            <option key={m} value={m}>
              {m}월
            </option>
          ))}
        </select>

        {/* V130.3.1: 이동 버튼 (화살표 제거) */}
        <button
          type="button"
          onClick={handleJump}
          disabled={disabled || !isFilterDifferent}
          style={{
            padding: '6px 14px',
            fontSize: '14px',
            fontWeight: 600,
            border: 'none',
            borderRadius: '4px',
            backgroundColor: isFilterDifferent ? 'var(--color-primary)' : 'var(--color-gray-200)',
            color: isFilterDifferent ? 'white' : 'var(--color-gray-400)',
            cursor: disabled || !isFilterDifferent ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1,
            transition: 'background-color 0.2s, color 0.2s',
          }}
          title={isFilterDifferent ? `${filterYear}년 ${filterMonth}월로 이동` : '현재 표시 중인 월입니다'}
        >
          이동
        </button>
      </div>

      {/* V54.10: 공휴일 정보 + 근무일수 (우측) */}
      <div
        style={{
          marginLeft: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
        }}
      >
        {/* 공휴일 정보 (테두리 없이, 텍스트 길이에 맞게) */}
        {holidays.length > 0 && (
          <span
            style={{
              fontSize: '13px',
              color: 'var(--color-gray-600)',
            }}
          >
            <span style={{ color: 'var(--color-gray-500)' }}>공휴일:</span>{' '}
            {holidays.map((h) => `${h.date.substring(5)} ${h.name}`).join(', ')}
          </span>
        )}

        {/* 근무일수 */}
        {workingDays !== undefined && (
          <div
            style={{
              padding: '6px 12px',
              backgroundColor: 'var(--color-primary-100)',
              color: 'var(--color-primary-700)',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              whiteSpace: 'nowrap',
            }}
          >
            근무일수: {workingDays}일
          </div>
        )}
      </div>
    </div>
  );
}
